import { Component, OnInit } from "@angular/core";
import { AiSettingsService } from "../../services/ai-settings.service";
import { AiConnection } from "../../models/chat.models";

import { FormsModule } from "@angular/forms";
import { MaterialModule } from "../../material.module";
import { LeftMenuComponent } from "../../menu/left-menu/left-menu.component";
import { SpinnerComponent } from "../../shared/spinner/spinner.component";
import { AiConnectionsSkeletonComponent } from "./ai-connections-skeleton/ai-connections-skeleton.component";

@Component({
  selector: "modernization-ai-connections",
  templateUrl: "./ai-connections.component.html",
  styleUrls: ['./ai-connections.component.scss'],
  imports: [FormsModule, MaterialModule, LeftMenuComponent, SpinnerComponent, AiConnectionsSkeletonComponent]
})
export class AiConnectionsComponent implements OnInit {
  // Connections
  connections: AiConnection[] = [];
  editingConnection: AiConnection | null = null;
  isCreatingConnection = false;

  // UI state
  loading = false;
  saving = false;
  error: string | null = null;
  successMessage: string | null = null;

  constructor(private aiSettingsService: AiSettingsService) {}

  ngOnInit(): void {
    this.loadConnections();
  }

  loadConnections(): void {
    this.loading = true;
    this.error = null;

    this.aiSettingsService.getAllConnections().subscribe({
      next: (connections) => {
        this.connections = connections;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading connections:', err);
        this.loading = false;
        this.error = 'Failed to load connections';
      }
    });
  }

  startCreateConnection(): void {
    this.isCreatingConnection = true;
    this.editingConnection = {
      id: 0,
      name: '',
      endpoint: '',
      modelName: '',
      deployment: '',
      apiVersion: 'v1',
      apiMode: 'Responses',
      reasoningEffort: 'medium',
      maxCompletionTokens: 16384,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  editConnection(connection: AiConnection): void {
    this.isCreatingConnection = false;
    this.editingConnection = {
      ...connection,
      apiMode: connection.apiMode ?? 'ChatCompletions',
      reasoningEffort: connection.reasoningEffort ?? null
    };
  }

  cancelEditConnection(): void {
    this.editingConnection = null;
    this.isCreatingConnection = false;
  }

  saveConnection(): void {
    if (!this.editingConnection) return;

    // Validation
    if (!this.editingConnection.name || !this.editingConnection.endpoint ||
        !this.editingConnection.modelName || !this.editingConnection.deployment) {
      this.error = 'Please fill in all required connection fields';
      return;
    }

    const model = (this.editingConnection.modelName || '').toLowerCase();
    const deployment = (this.editingConnection.deployment || '').toLowerCase();
    if (this.editingConnection.apiMode !== 'Responses' && this.editingConnection.apiMode !== 'ChatCompletions') {
      this.error = 'Select Responses or Chat Completions.';
      return;
    }
    if (this.editingConnection.reasoningEffort?.toLowerCase() === 'none'
        && (model.startsWith('gpt-6') || deployment.startsWith('gpt-6'))) {
      this.error = "Reasoning effort 'none' is not supported for GPT-6 models";
      return;
    }

    if (!this.isValidFoundryEndpoint(this.editingConnection.endpoint)) {
      this.error = 'Use an HTTPS Foundry endpoint or a Chevron gateway base URL ending in /ai-models/ai-realtime-inference-<id>/v1. Credentials, query strings, fragments and custom ports are not supported.';
      return;
    }

    this.saving = true;
    this.error = null;
    this.successMessage = null;

    const operation = this.isCreatingConnection
      ? this.aiSettingsService.createConnection(this.editingConnection)
      : this.aiSettingsService.updateConnection(this.editingConnection);

    operation.subscribe({
      next: () => {
        this.successMessage = `Connection ${this.isCreatingConnection ? 'created' : 'updated'} successfully!`;
        this.saving = false;
        this.editingConnection = null;
        this.isCreatingConnection = false;
        this.loadConnections();
        setTimeout(() => this.successMessage = null, 5000);
      },
      error: (err) => {
        console.error('Error saving connection:', err);
        this.error = err.error?.message || 'Failed to save connection';
        this.saving = false;
      }
    });
  }

  private isValidFoundryEndpoint(endpoint: string): boolean {
    try {
      const url = new URL(endpoint);

      if (url.protocol !== 'https:') return false;
      if (url.username || url.password) return false;
      if (url.search) return false;
      if (url.hash) return false;
      if (url.port && url.port !== '443') return false;

      const hostname = url.hostname.toLowerCase();

      if (hostname.endsWith('.services.ai.azure.com')) {
        return true;
      }

      if (hostname === 'apim.chevron.com') {
        const path = url.pathname || '/';
        const chevronRegex = /^\/ai-models\/ai-realtime-inference-[a-z0-9-]+\/v1\/?$/;
        return chevronRegex.test(path);
      }

      return false;
    } catch {
      return false;
    }
  }

  deleteConnection(connection: AiConnection): void {
    if (!confirm(`Are you sure you want to delete the connection "${connection.name}"?`)) {
      return;
    }

    this.saving = true;
    this.error = null;

    this.aiSettingsService.deleteConnection(connection.id).subscribe({
      next: () => {
        this.successMessage = 'Connection deleted successfully!';
        this.saving = false;
        this.loadConnections();
        setTimeout(() => this.successMessage = null, 5000);
      },
      error: (err) => {
        console.error('Error deleting connection:', err);
        this.error = err.error?.message || 'Failed to delete connection. It may be in use by a feature.';
        this.saving = false;
      }
    });
  }
}
